import { useRef, useState, useCallback, useEffect } from 'react';

const WS_RECONNECT_DELAY = 3000;
const WS_PING_INTERVAL = 25000;

export function useWebSocket(projectId) {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const reconnectRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const handlersRef = useRef({});

  const on = useCallback((eventType, handler) => {
    handlersRef.current[eventType] = handler;
  }, []);

  const emit = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const connect = useCallback(() => {
    if (!projectId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Build WS URL from the backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/${projectId}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Send auth token as first message
        ws.send(JSON.stringify({ token }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          setLastEvent(msg);

          if (msg.type === 'connected') {
            setConnected(true);
            // Start ping interval
            pingRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, WS_PING_INTERVAL);
          }

          // Call registered handler
          const handler = handlersRef.current[msg.type];
          if (handler) handler(msg.data || msg);
        } catch (e) {
          console.error('WS message parse error:', e);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingRef.current);
        // Auto-reconnect
        reconnectRef.current = setTimeout(() => {
          connect();
        }, WS_RECONNECT_DELAY);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      console.error('WS connection error:', e);
    }
  }, [projectId]);

  const disconnect = useCallback(() => {
    clearInterval(pingRef.current);
    clearTimeout(reconnectRef.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  // Auto-connect when projectId changes
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [projectId, connect, disconnect]);

  return { connected, lastEvent, on, emit, disconnect };
}
